import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../../api/person/queries";
import { personTyperForm } from "../../../../../config/form/person/personTyperFormConfig";
import { defaultPersonTyperColumns } from "../../../../../config/person";
import { getHeaderActiveColumns, TD } from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import ActiveIcon from "../../../../icons/ActiveIcon";
import TableBasic, { TableRow } from "../../../../Table/TableBasic";
import IngenRegistreringer from "../IngenRegistreringer";
import {
  Maybe,
  PersonDto,
  PersonPersontype,
} from "../../../../../interfaces/schema";
import { PersonTyperField } from "../../../../../interfaces/personer";
import { IColumn, SortBy } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { periodeIsActive } from "../../../../../util/date";
import { orderBy } from "../../../../../util/orderBy";

export default function Persontyper({
  columns,
  invalidateData,
  personId,
  personTyper,
  setActiveColumns,
}: {
  personId: string;
  columns: IColumn<PersonTyperField>[];
  setActiveColumns: React.Dispatch<React.SetStateAction<PersonTyperField[]>>;
  personTyper: Maybe<PersonPersontype[]> | undefined;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const isAuth = hasPermission(user, "BasisWrite");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personPersontyperSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  const { data } = useQuery<{
    person: PersonDto;
  }>(PERSON_BY_ID_QUERY(), {
    variables: { id: personId },
  });

  // event handlers
  function downloadHandler() {
    tableToXLSX(columns, personTyper ?? [], {
      fileName: `persontyper-for-${data?.person.kundeNr}`,
      additionalData: {
        kundenr: data?.person.kundeNr,
      },
    });
  }

  if (!personTyper?.length) {
    return (
      <IngenRegistreringer
        editConfig={isAuth ? personTyperForm({ personId }) : undefined}
        invalidateData={invalidateData}
      />
    );
  }

  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <TableBasic
        defaultColumns={defaultPersonTyperColumns}
        columns={columns}
        setActiveColumns={setActiveColumns}
        headerRow={
          <>
            <th className="indicator"></th>
            {getHeaderActiveColumns(columns, {
              defaultSortBy,
              setSortBy,
              sortBy,
            })}
          </>
        }
        editConfig={isAuth ? personTyperForm({ personId }) : undefined}
        invalidateData={invalidateData}
      >
        {orderBy(
          personTyper,
          sortBy.map((s) => s.field),
          sortBy.map((s) => s.direction),
        ).map((type) => {
          const { id } = type;
          return (
            <TableRow
              key={id}
              editConfig={
                isAuth
                  ? personTyperForm({ personId, personPersonTypeId: id })
                  : undefined
              }
              invalidateData={invalidateData}
            >
              <td
                className={`indicator ${
                  periodeIsActive(
                    new Date(type.periode.fraDato),
                    new Date(type.periode.tilDatoInklusiv),
                  )
                    ? " has-effect"
                    : ""
                }  `}
              >
                <ActiveIcon />
              </td>
              <>
                {columns
                  .filter((col) => col.active)
                  .map((col) => {
                    return <TD key={col.field} col={col} rowData={type} />;
                  })}
              </>
            </TableRow>
          );
        })}
      </TableBasic>
    </>
  );
}

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];
